import {toastTypes} from '../../../../helpers/constants.js'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button, Table} from "react-bootstrap";
import InputGroupWithMessages from "../../../shared/inputgroupwithmessages/InputGroupWithMessages.js";
import ComboBoxWithMessages from "../../../shared/comboboxwithmessages/ComboBoxWithMessages.js";
import {getParameterToShow, updateFieldIfChanged} from "../../../../helpers/settingsHelper.js";
import {addParameterIfChanged, validateParameter} from "../../../../helpers/awareSettingsHelper.js";
import {awareParamNames} from "../../../parameters/awareParamSpec.js";
import {cloneDeep, update} from "lodash";
import PopOverDialog from "../../../shared/popoverdialog/PopOverDialog.js";
import {addToast} from "../../../../helpers/reduxstore/reducers/toastReducer.js";
import {useDispatch} from "react-redux";
import useUserRole from "../../../../helpers/hooks/useUserRole.js";
import {isBasic} from "../../../../helpers/authHelper.js";
import {CheckCircle, ExclamationTriangle} from "react-bootstrap-icons";
import DeviceConfigTopInformation from "../../../shared/topinformation/DeviceConfigTopInformation.js";
import SettingsSubHeader from "../../settingscomponents/SettingsSubHeader.js";
import SwitchGroup from "../../../shared/switchgroup/SwitchGroup.js";
import Accordion from 'react-bootstrap/Accordion';

/** Displays the configuration page for filter settings
 * @param {object} device device object
 * @param {string} className classes for the component wrapper
 * @param {function} save save function to trigger displaying group selector or confirmation (for single device) */
export default function AwareFilterSettings({device, className, save}) {
    const {t} = useTranslation(['settingspage', 'common', 'telemetry', 'locales'])
    const dispatch = useDispatch()
    const userRole = useUserRole();
    const [oldDeviceState, setOldDeviceState] = useState(cloneDeep(device))

    const firmwareVersion = device?.device_twin?.reported?.applicationVersion === '0.9.0'
        ? '1.6.0'
        : device?.device_twin?.reported?.applicationVersion;

    const isVersionAtLeast = (version, minimumVersion) => {
        const versionParts = (version || '').split('.').map((part) => Number.parseInt(part, 10));
        const minimumParts = (minimumVersion || '').split('.').map((part) => Number.parseInt(part, 10));
        const maxLength = Math.max(versionParts.length, minimumParts.length);

        for (let i = 0; i < maxLength; i++) {
            const versionPart = Number.isNaN(versionParts[i]) ? 0 : (versionParts[i] ?? 0);
            const minimumPart = Number.isNaN(minimumParts[i]) ? 0 : (minimumParts[i] ?? 0);
            if (versionPart > minimumPart) return true;
            if (versionPart < minimumPart) return false;
        }

        return true;
    };

    const getLifetimeWarningParamName = (paramNameNew, paramNameOld) => isVersionAtLeast(firmwareVersion, '1.6.0')
        ? paramNameNew : paramNameOld;

    const preFilterSupMaxLifetimeWarningParamName = getLifetimeWarningParamName(awareParamNames.pre_filter_sup_max_lifetime_warning, awareParamNames.pre_filter_sup_max_lifetime_warning_period);
    const preFilterExtMaxLifetimeWarningParamName = getLifetimeWarningParamName(awareParamNames.pre_filter_ext_max_lifetime_warning, awareParamNames.pre_filter_ext_max_lifetime_warning_period);
    const mainFilterSupMaxLifetimeWarningParamName = getLifetimeWarningParamName(awareParamNames.main_filter_sup_max_lifetime_warning, awareParamNames.main_filter_sup_max_lifetime_warning_period);
    const mainFilterExtMaxLifetimeWarningParamName = getLifetimeWarningParamName(awareParamNames.main_filter_ext_max_lifetime_warning, awareParamNames.main_filter_ext_max_lifetime_warning_period);

    const intake_filter_pre_filter_max_life_time_warning_mark = isVersionAtLeast(firmwareVersion, '1.6.0') ? t('locales:intake_filter_pre_filter_time_warning_question_mark') : t('settingspage:infomodals.filter_max_life_time_warning');
    const intake_filter_main_filter_max_life_time_warning_mark = isVersionAtLeast(firmwareVersion, '1.6.0') ? t('locales:intake_filter_main_filter_time_warning_question_mark') : t('settingspage:infomodals.filter_max_life_time_warning');
    const extract_filter_pre_filter_max_life_time_warning_mark = isVersionAtLeast(firmwareVersion, '1.6.0') ? t('locales:extract_filter_pre_filter_time_warning_question_mark') : t('settingspage:infomodals.filter_max_life_time_warning');
    const extract_filter_main_filter_max_life_time_warning_mark = isVersionAtLeast(firmwareVersion, '1.6.0') ? t('locales:extract_filter_main_filter_time_warning_question_mark') : t('settingspage:infomodals.filter_max_life_time_warning');

    const system_alarms = device?.latest_telemetry?.system_alarms;
    const system_warnings = device?.latest_telemetry?.system_warnings;
    const filter_alarm_exists = system_alarms === undefined ? false : system_alarms.some(alarm => alarm.message.toLowerCase().includes("filter"));
    const filter_warning_exists = system_warnings === undefined ? false : system_warnings.some(warning => warning.message.toLowerCase().includes("filter"));

    //Intake pre-filter
    const pre_filter_supply_operating_time = device?.latest_telemetry?.pre_filter_supply_operating_time;
    let pre_filter_supply_operating_time_hours = pre_filter_supply_operating_time;
    if (pre_filter_supply_operating_time > 0) {
        pre_filter_supply_operating_time_hours = Math.floor(pre_filter_supply_operating_time / 3600);
    }

    const pre_filter_supply_time_in_service = device?.latest_telemetry?.pre_filter_supply_time_in_service;
    const pre_filter_supply_current_rpm_deviation = device?.latest_telemetry?.pre_filter_supply_current_rpm_deviation;
    
    //Extract pre-filter
    const pre_filter_extraction_operating_time = device?.latest_telemetry?.pre_filter_extraction_operating_time;
    let pre_filter_extraction_operating_time_hours = pre_filter_extraction_operating_time;
    if (pre_filter_extraction_operating_time > 0) {
        pre_filter_extraction_operating_time_hours = Math.floor(pre_filter_extraction_operating_time / 3600);
    }

    const pre_filter_extraction_time_in_service = device?.latest_telemetry?.pre_filter_extraction_time_in_service;
    const pre_filter_extraction_current_rpm_deviation = device?.latest_telemetry?.pre_filter_extraction_current_rpm_deviation;

    //Intake main filter
    const main_filter_supply_operating_time = device?.latest_telemetry?.main_filter_supply_operating_time;
    let main_filter_supply_operating_time_hours = main_filter_supply_operating_time;
    if (main_filter_supply_operating_time > 0) {
        main_filter_supply_operating_time_hours = Math.floor(main_filter_supply_operating_time / 3600);
    }

    const main_filter_supply_time_in_service = device?.latest_telemetry?.main_filter_supply_time_in_service;
    const main_filter_supply_current_rpm_deviation = device?.latest_telemetry?.main_filter_supply_current_rpm_deviation;

    //Extract main filter
    const main_filter_extraction_operating_time = device?.latest_telemetry?.main_filter_extraction_operating_time;
    let main_filter_extraction_operating_time_hours = main_filter_extraction_operating_time;
    if (main_filter_extraction_operating_time > 0) {
        main_filter_extraction_operating_time_hours = Math.floor(main_filter_extraction_operating_time / 3600);
    }

    const main_filter_extraction_time_in_service = device?.latest_telemetry?.main_filter_extraction_time_in_service;
    const main_filter_extraction_current_rpm_deviation = device?.latest_telemetry?.main_filter_extraction_current_rpm_deviation;
          
    const getFilterLatestStatus = () => {
        if (filter_alarm_exists) return "ALARM";
        if (filter_warning_exists) return "WARNING"; // filter change needed
        return "GOOD";
    }

    const renderFilterTestModeComboBox = () => (
        <ComboBoxWithMessages
            className='p-4 m-auto'
            label={t('locales:filter_settings_filter_monitoring_method_header')}
            inputValue={e => setFilterTestMode({ value: e.target.value })}
            value={filterTestMode.value}
            options={[
            { id: 0, option: t('settingspage:filter_settings.filter_test_mode_options.off') },
            { id: 1, option: t('settingspage:filter_settings.filter_test_mode_options.aware_timer') },
            { id: 2, option: t('settingspage:filter_settings.filter_test_mode_options.aware_lifetime') },
            { id: 3, option: t('settingspage:filter_settings.filter_test_mode_options.aware_timer_and_lifetime') },
            { id: 4, option: t('settingspage:filter_settings.filter_test_mode_options.aware_timer_lifetime_and_pressure') },
            { id: 5, option: t('settingspage:filter_settings.filter_test_mode_options.aware_pressure') }
            ]}
            messages={filterTestMode.messages}
            infoelement={
            <PopOverDialog
                popOverTitel={t('locales:filter_settings_filter_monitoring_method_header')}
                popOverContent={t('locales:filter_settings_filter_monitoring_method_question_mark')}
                trigger={['hover', 'touch']}
                contentAsHtml={true}
            />
            }
        />
    );

    const [sameExtractAsIntake, setSameExtractAsIntake] = useState(false);
    const [sameIntakeAsExtract, setSameIntakeAsExtract] = useState(false);
    const [preFilterSupplySelected, setPreFilterSupplySelected] = useState(false);
    const [preFilterExtractionSelected, setPreFilterExtractionSelected] = useState(false);
    const [mainFilterSupplySelected, setMainFilterSupplySelected] = useState(false);
    const [mainFilterExtractionSelected, setMainFilterExtractionSelected] = useState(false);

    const [filterLatestStatus, setFilterLatestStatus] = useState(getFilterLatestStatus(device))
    const [filterTestMode, setFilterTestMode] = useState(getParameterToShow(device, awareParamNames.filter_test_mode, t))
    
    //Intake pre-filter
    const [ preFilterSupHoursWarning, setPreFilterSupHoursWarning] = useState(getParameterToShow(device, awareParamNames.pre_filter_sup_hours_warning, t))
    const [ preFilterSupHoursAlarm, setPreFilterSupHoursAlarm] = useState(getParameterToShow(device, awareParamNames.pre_filter_sup_hours_alarm, t))
    const [ preFilterSupMaxLifetimeWarningPeriod, setPreFilterSupMaxLifetimeWarningPeriod] = useState(getParameterToShow(device, preFilterSupMaxLifetimeWarningParamName, t))
    const [ preFilterSupMaxLifetimeAlarm, setPreFilterSupMaxLifetimeAlarm] = useState(getParameterToShow(device, awareParamNames.pre_filter_sup_max_lifetime_alarm, t))
    const [ preFilterSupRpmWarningDeviationPct, setPreFilterSupRpmWarningDeviationPct] = useState(getParameterToShow(device, awareParamNames.pre_filter_sup_rpm_warning_deviation_pct, t))
    const [ preFilterSupRpmAlarmDeviationPct, setPreFilterSupRpmAlarmDeviationPct] = useState(getParameterToShow(device, awareParamNames.pre_filter_sup_rpm_alarm_deviation_pct, t))

    //Extract pre-filter
    const [ preFilterExtHoursWarning, setPreFilterExtHoursWarning] = useState(getParameterToShow(device, awareParamNames.pre_filter_ext_hours_warning, t))
    const [ preFilterExtHoursAlarm, setPreFilterExtHoursAlarm] = useState(getParameterToShow(device, awareParamNames.pre_filter_ext_hours_alarm, t))
    const [ preFilterExtMaxLifetimeWarningPeriod, setPreFilterExtMaxLifetimeWarningPeriod] = useState(getParameterToShow(device, preFilterExtMaxLifetimeWarningParamName, t))
    const [ preFilterExtMaxLifetimeAlarm, setPreFilterExtMaxLifetimeAlarm] = useState(getParameterToShow(device, awareParamNames.pre_filter_ext_max_lifetime_alarm, t))
    const [ preFilterExtRpmWarningDeviationPct, setPreFilterExtRpmWarningDeviationPct] = useState(getParameterToShow(device, awareParamNames.pre_filter_ext_rpm_warning_deviation_pct, t))
    const [ preFilterExtRpmAlarmDeviationPct, setPreFilterExtRpmAlarmDeviationPct] = useState(getParameterToShow(device, awareParamNames.pre_filter_ext_rpm_alarm_deviation_pct, t))

    //Intake main filter
    const [ mainFilterSupHoursWarning, setMainFilterSupHoursWarning] = useState(getParameterToShow(device, awareParamNames.main_filter_sup_hours_warning, t))
    const [ mainFilterSupHoursAlarm, setMainFilterSupHoursAlarm] = useState(getParameterToShow(device, awareParamNames.main_filter_sup_hours_alarm, t))
    const [ mainFilterSupMaxLifetimeWarningPeriod, setMainFilterSupMaxLifetimeWarningPeriod] = useState(getParameterToShow(device, mainFilterSupMaxLifetimeWarningParamName, t))
    const [ mainFilterSupMaxLifetimeAlarm, setMainFilterSupMaxLifetimeAlarm] = useState(getParameterToShow(device, awareParamNames.main_filter_sup_max_lifetime_alarm, t))
    const [ mainFilterSupRpmWarningDeviationPct, setMainFilterSupRpmWarningDeviationPct] = useState(getParameterToShow(device, awareParamNames.main_filter_sup_rpm_warning_deviation_pct, t))
    const [ mainFilterSupRpmAlarmDeviationPct, setMainFilterSupRpmAlarmDeviationPct] = useState(getParameterToShow(device, awareParamNames.main_filter_sup_rpm_alarm_deviation_pct, t))

    //Extract main filter
    const [ mainFilterExtHoursWarning, setMainFilterExtHoursWarning] = useState(getParameterToShow(device, awareParamNames.main_filter_ext_hours_warning, t))
    const [ mainFilterExtHoursAlarm, setMainFilterExtHoursAlarm] = useState(getParameterToShow(device, awareParamNames.main_filter_ext_hours_alarm, t))
    const [ mainFilterExtMaxLifetimeWarningPeriod, setMainFilterExtMaxLifetimeWarningPeriod] = useState(getParameterToShow(device, mainFilterExtMaxLifetimeWarningParamName, t))
    const [ mainFilterExtMaxLifetimeAlarm, setMainFilterExtMaxLifetimeAlarm] = useState(getParameterToShow(device, awareParamNames.main_filter_ext_max_lifetime_alarm, t))
    const [ mainFilterExtRpmWarningDeviationPct, setMainFilterExtRpmWarningDeviationPct] = useState(getParameterToShow(device, awareParamNames.main_filter_ext_rpm_warning_deviation_pct, t))
    const [ mainFilterExtRpmAlarmDeviationPct, setMainFilterExtRpmAlarmDeviationPct] = useState(getParameterToShow(device, awareParamNames.main_filter_ext_rpm_alarm_deviation_pct, t))

    function getCellClass({
        value,
        warning,
        alarm,
        baseClass = 'align-middle text-center hide-on-mobile',
        warningClass = 'bg-warning-dim',
        alarmClass = 'bg-danger-dim',
        warningOp = (v, w) => v >= w,
        alarmOp = (v, a) => v >= a
    }) {
        let cls = baseClass;
        const v = Number(value);
        const w = Number(warning);
        const a = Number(alarm);
        if (!isNaN(v)) {
            if (!isNaN(w) && warningOp(v, isVersionAtLeast(firmwareVersion, '1.6.0') ? w : a-w)) {
                cls += ' ' + warningClass;
            }
            else if (!isNaN(a) && alarmOp(v, a)) {
                cls += ' ' + alarmClass;
            } 
        }
        return cls;
    }

    const formatRoundedPercentage = (value) => {
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? value : Math.round(numericValue);
    }

    // Intake pre-filter
    const preFilterSupplyOperatingTimeClass = getCellClass({
        value: pre_filter_supply_operating_time_hours,
        warning: preFilterSupHoursWarning.value,
        alarm: preFilterSupHoursAlarm.value
    });

    const preFilterSupplyLifetimeClass = getCellClass({
        value: pre_filter_supply_time_in_service,
        warning: preFilterSupMaxLifetimeWarningPeriod.value,
        alarm: preFilterSupMaxLifetimeAlarm.value
    });

    const preFilterSupplyRpmDeviationClass = getCellClass({
        value: pre_filter_supply_current_rpm_deviation,
        warning: preFilterSupRpmWarningDeviationPct.value,
        alarm: preFilterSupRpmAlarmDeviationPct.value
    });

    // Extract pre-filter
    const preFilterExtractOperatingTimeClass = getCellClass({
        value: pre_filter_extraction_operating_time_hours,
        warning: preFilterExtHoursWarning.value,
        alarm: preFilterExtHoursAlarm.value
    });

    const preFilterExtractLifetimeClass = getCellClass({
        value: pre_filter_extraction_time_in_service,
        warning: preFilterExtMaxLifetimeWarningPeriod.value,
        alarm: preFilterExtMaxLifetimeAlarm.value
    });

    const preFilterExtractRpmDeviationClass = getCellClass({
        value: pre_filter_extraction_current_rpm_deviation,
        warning: preFilterExtRpmWarningDeviationPct.value,
        alarm: preFilterExtRpmAlarmDeviationPct.value
    });

    // Intake main filter
    const mainFilterSupplyOperatingTimeClass = getCellClass({
        value: main_filter_supply_operating_time_hours,
        warning: mainFilterSupHoursWarning.value,
        alarm: mainFilterSupHoursAlarm.value
    });

    const mainFilterSupplyLifetimeClass = getCellClass({
        value: main_filter_supply_time_in_service,
        warning: mainFilterSupMaxLifetimeWarningPeriod.value,
        alarm: mainFilterSupMaxLifetimeAlarm.value
    });

    const mainFilterSupplyRpmDeviationClass = getCellClass({
        value: main_filter_supply_current_rpm_deviation,
        warning: mainFilterSupRpmWarningDeviationPct.value,
        alarm: mainFilterSupRpmAlarmDeviationPct.value
    });

    // Extract main filter
    const mainFilterExtractOperatingTimeClass = getCellClass({
        value: main_filter_extraction_operating_time_hours,
        warning: mainFilterExtHoursWarning.value,
        alarm: mainFilterExtHoursAlarm.value
    });

    const mainFilterExtractLifetimeClass = getCellClass({
        value: main_filter_extraction_time_in_service,
        warning: mainFilterExtMaxLifetimeWarningPeriod.value,
        alarm: mainFilterExtMaxLifetimeAlarm.value
    });

    const mainFilterExtractRpmDeviationClass = getCellClass({
        value: main_filter_extraction_current_rpm_deviation,
        warning: mainFilterExtRpmWarningDeviationPct.value,
        alarm: mainFilterExtRpmAlarmDeviationPct.value
    });
    
    useEffect(() => {
        // When device is updated after write, update parameters to show / hide messages
        setFilterLatestStatus(getFilterLatestStatus(device))
        updateFieldIfChanged(setFilterTestMode, oldDeviceState, device, awareParamNames.filter_test_mode, t)
        updateFieldIfChanged(setPreFilterSupHoursWarning, oldDeviceState, device, awareParamNames.pre_filter_sup_hours_warning, t)
        updateFieldIfChanged(setPreFilterSupHoursAlarm, oldDeviceState, device, awareParamNames.pre_filter_sup_hours_alarm, t)
        updateFieldIfChanged(setPreFilterSupMaxLifetimeWarningPeriod, oldDeviceState, device, preFilterSupMaxLifetimeWarningParamName, t)
        updateFieldIfChanged(setPreFilterSupMaxLifetimeAlarm, oldDeviceState, device, awareParamNames.pre_filter_sup_max_lifetime_alarm, t)
        updateFieldIfChanged(setPreFilterSupRpmWarningDeviationPct, oldDeviceState, device, awareParamNames.pre_filter_sup_rpm_warning_deviation_pct, t)
        updateFieldIfChanged(setPreFilterSupRpmAlarmDeviationPct, oldDeviceState, device, awareParamNames.pre_filter_sup_rpm_alarm_deviation_pct, t)
        updateFieldIfChanged(setPreFilterExtHoursWarning, oldDeviceState, device, awareParamNames.pre_filter_ext_hours_warning, t)
        updateFieldIfChanged(setPreFilterExtHoursAlarm, oldDeviceState, device, awareParamNames.pre_filter_ext_hours_alarm, t)
        updateFieldIfChanged(setPreFilterExtMaxLifetimeWarningPeriod, oldDeviceState, device, preFilterExtMaxLifetimeWarningParamName, t)
        updateFieldIfChanged(setPreFilterExtMaxLifetimeAlarm, oldDeviceState, device, awareParamNames.pre_filter_ext_max_lifetime_alarm, t)
        updateFieldIfChanged(setPreFilterExtRpmWarningDeviationPct, oldDeviceState, device, awareParamNames.pre_filter_ext_rpm_warning_deviation_pct, t)
        updateFieldIfChanged(setPreFilterExtRpmAlarmDeviationPct, oldDeviceState, device, awareParamNames.pre_filter_ext_rpm_alarm_deviation_pct, t)
        updateFieldIfChanged(setMainFilterSupHoursWarning, oldDeviceState, device, awareParamNames.main_filter_sup_hours_warning, t)
        updateFieldIfChanged(setMainFilterSupHoursAlarm, oldDeviceState, device, awareParamNames.main_filter_sup_hours_alarm, t)
        updateFieldIfChanged(setMainFilterSupMaxLifetimeWarningPeriod, oldDeviceState, device, mainFilterSupMaxLifetimeWarningParamName, t)
        updateFieldIfChanged(setMainFilterSupMaxLifetimeAlarm, oldDeviceState, device, awareParamNames.main_filter_sup_max_lifetime_alarm, t)
        updateFieldIfChanged(setMainFilterSupRpmWarningDeviationPct, oldDeviceState, device, awareParamNames.main_filter_sup_rpm_warning_deviation_pct, t)
        updateFieldIfChanged(setMainFilterSupRpmAlarmDeviationPct, oldDeviceState, device, awareParamNames.main_filter_sup_rpm_alarm_deviation_pct, t)
        updateFieldIfChanged(setMainFilterExtHoursWarning, oldDeviceState, device, awareParamNames.main_filter_ext_hours_warning, t)
        updateFieldIfChanged(setMainFilterExtHoursAlarm, oldDeviceState, device, awareParamNames.main_filter_ext_hours_alarm, t)
        updateFieldIfChanged(setMainFilterExtMaxLifetimeWarningPeriod, oldDeviceState, device, mainFilterExtMaxLifetimeWarningParamName, t)
        updateFieldIfChanged(setMainFilterExtMaxLifetimeAlarm, oldDeviceState, device, awareParamNames.main_filter_ext_max_lifetime_alarm, t)
        updateFieldIfChanged(setMainFilterExtRpmWarningDeviationPct, oldDeviceState, device, awareParamNames.main_filter_ext_rpm_warning_deviation_pct, t)
        updateFieldIfChanged(setMainFilterExtRpmAlarmDeviationPct, oldDeviceState, device, awareParamNames.main_filter_ext_rpm_alarm_deviation_pct, t)
        setOldDeviceState(cloneDeep(device))

        

    }, [device])

     // Sync intake filter values with extract filter values when sameIntakeAsExtract is true
    useEffect(() => {
        if (sameIntakeAsExtract) {
            setPreFilterSupHoursWarning(preFilterExtHoursWarning);
            setPreFilterSupHoursAlarm(preFilterExtHoursAlarm);
            setPreFilterSupMaxLifetimeWarningPeriod(preFilterExtMaxLifetimeWarningPeriod);
            setPreFilterSupMaxLifetimeAlarm(preFilterExtMaxLifetimeAlarm);
            setPreFilterSupRpmWarningDeviationPct(preFilterExtRpmWarningDeviationPct);
            setPreFilterSupRpmAlarmDeviationPct(preFilterExtRpmAlarmDeviationPct);
            setMainFilterSupHoursWarning(mainFilterExtHoursWarning);
            setMainFilterSupHoursAlarm(mainFilterExtHoursAlarm);
            setMainFilterSupMaxLifetimeWarningPeriod(mainFilterExtMaxLifetimeWarningPeriod);
            setMainFilterSupMaxLifetimeAlarm(mainFilterExtMaxLifetimeAlarm);
            setMainFilterSupRpmWarningDeviationPct(mainFilterExtRpmWarningDeviationPct);
            setMainFilterSupRpmAlarmDeviationPct(mainFilterExtRpmAlarmDeviationPct);
        }
    }, [sameIntakeAsExtract, preFilterExtHoursWarning, preFilterExtHoursAlarm, preFilterExtMaxLifetimeWarningPeriod, preFilterExtMaxLifetimeAlarm, preFilterExtRpmWarningDeviationPct, preFilterExtRpmAlarmDeviationPct, mainFilterExtHoursWarning, mainFilterExtHoursAlarm, mainFilterExtMaxLifetimeWarningPeriod, mainFilterExtMaxLifetimeAlarm, mainFilterExtRpmWarningDeviationPct, mainFilterExtRpmAlarmDeviationPct]);

         // Sync extract filter values with intake filter values when sameExtractAsIntake is true
    useEffect(() => {
        if (sameExtractAsIntake) {
            setPreFilterExtHoursWarning(preFilterSupHoursWarning);
            setPreFilterExtHoursAlarm(preFilterSupHoursAlarm);
            setPreFilterExtMaxLifetimeWarningPeriod(preFilterSupMaxLifetimeWarningPeriod);
            setPreFilterExtMaxLifetimeAlarm(preFilterSupMaxLifetimeAlarm);
            setPreFilterExtRpmWarningDeviationPct(preFilterSupRpmWarningDeviationPct);
            setPreFilterExtRpmAlarmDeviationPct(preFilterSupRpmAlarmDeviationPct);
            setMainFilterExtHoursWarning(mainFilterSupHoursWarning);
            setMainFilterExtHoursAlarm(mainFilterSupHoursAlarm);
            setMainFilterExtMaxLifetimeWarningPeriod(mainFilterSupMaxLifetimeWarningPeriod);
            setMainFilterExtMaxLifetimeAlarm(mainFilterSupMaxLifetimeAlarm);
            setMainFilterExtRpmWarningDeviationPct(mainFilterSupRpmWarningDeviationPct);
            setMainFilterExtRpmAlarmDeviationPct(mainFilterSupRpmAlarmDeviationPct);
        }
    }, [sameExtractAsIntake, preFilterSupHoursWarning, preFilterSupHoursAlarm, preFilterSupMaxLifetimeWarningPeriod, preFilterSupMaxLifetimeAlarm, preFilterSupRpmWarningDeviationPct, preFilterSupRpmAlarmDeviationPct, mainFilterSupHoursWarning, mainFilterSupHoursAlarm, mainFilterSupMaxLifetimeWarningPeriod, mainFilterSupMaxLifetimeAlarm, mainFilterSupRpmWarningDeviationPct, mainFilterSupRpmAlarmDeviationPct]);

    /**
     * validates parameters. Adds messages to viewmodels if fail. returns true if all is validated, otherwise false.
     * @returns {boolean}
     */
    const validateParameters = () => {
        let validatedList = []
        validatedList.push(validateParameter(awareParamNames.filter_test_mode, filterTestMode, setFilterTestMode, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_sup_hours_warning, preFilterSupHoursWarning, setPreFilterSupHoursWarning, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_sup_hours_alarm, preFilterSupHoursAlarm, setPreFilterSupHoursAlarm, t))
        validatedList.push(validateParameter(preFilterSupMaxLifetimeWarningParamName, preFilterSupMaxLifetimeWarningPeriod, setPreFilterSupMaxLifetimeWarningPeriod, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_sup_max_lifetime_alarm, preFilterSupMaxLifetimeAlarm, setPreFilterSupMaxLifetimeAlarm, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_sup_rpm_warning_deviation_pct, preFilterSupRpmWarningDeviationPct, setPreFilterSupRpmWarningDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_sup_rpm_alarm_deviation_pct, preFilterSupRpmAlarmDeviationPct, setPreFilterSupRpmAlarmDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_ext_hours_warning, preFilterExtHoursWarning, setPreFilterExtHoursWarning, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_ext_hours_alarm, preFilterExtHoursAlarm, setPreFilterExtHoursAlarm, t))
        validatedList.push(validateParameter(preFilterExtMaxLifetimeWarningParamName, preFilterExtMaxLifetimeWarningPeriod, setPreFilterExtMaxLifetimeWarningPeriod, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_ext_max_lifetime_alarm, preFilterExtMaxLifetimeAlarm, setPreFilterExtMaxLifetimeAlarm, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_ext_rpm_warning_deviation_pct, preFilterExtRpmWarningDeviationPct, setPreFilterExtRpmWarningDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.pre_filter_ext_rpm_alarm_deviation_pct, preFilterExtRpmAlarmDeviationPct, setPreFilterExtRpmAlarmDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_sup_hours_warning, mainFilterSupHoursWarning, setMainFilterSupHoursWarning, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_sup_hours_alarm, mainFilterSupHoursAlarm, setMainFilterSupHoursAlarm, t))
        validatedList.push(validateParameter(mainFilterSupMaxLifetimeWarningParamName, mainFilterSupMaxLifetimeWarningPeriod, setMainFilterSupMaxLifetimeWarningPeriod, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_sup_max_lifetime_alarm, mainFilterSupMaxLifetimeAlarm, setMainFilterSupMaxLifetimeAlarm, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_sup_rpm_warning_deviation_pct, mainFilterSupRpmWarningDeviationPct, setMainFilterSupRpmWarningDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_sup_rpm_alarm_deviation_pct, mainFilterSupRpmAlarmDeviationPct, setMainFilterSupRpmAlarmDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_ext_hours_warning, mainFilterExtHoursWarning, setMainFilterExtHoursWarning, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_ext_hours_alarm, mainFilterExtHoursAlarm, setMainFilterExtHoursAlarm, t))
        validatedList.push(validateParameter(mainFilterExtMaxLifetimeWarningParamName, mainFilterExtMaxLifetimeWarningPeriod, setMainFilterExtMaxLifetimeWarningPeriod, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_ext_max_lifetime_alarm, mainFilterExtMaxLifetimeAlarm, setMainFilterExtMaxLifetimeAlarm, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_ext_rpm_warning_deviation_pct, mainFilterExtRpmWarningDeviationPct, setMainFilterExtRpmWarningDeviationPct, t))
        validatedList.push(validateParameter(awareParamNames.main_filter_ext_rpm_alarm_deviation_pct, mainFilterExtRpmAlarmDeviationPct, setMainFilterExtRpmAlarmDeviationPct, t))
        return validatedList.every(v => v)
    }

    const getValuesToSave = () => {
        const updated_parameters = {}
        addParameterIfChanged(updated_parameters, awareParamNames.filter_test_mode, filterTestMode.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_sup_hours_warning, preFilterSupHoursWarning.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_sup_hours_alarm, preFilterSupHoursAlarm.value, device)
        addParameterIfChanged(updated_parameters, preFilterSupMaxLifetimeWarningParamName, preFilterSupMaxLifetimeWarningPeriod.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_sup_max_lifetime_alarm, preFilterSupMaxLifetimeAlarm.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_sup_rpm_warning_deviation_pct, preFilterSupRpmWarningDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_sup_rpm_alarm_deviation_pct, preFilterSupRpmAlarmDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_ext_hours_warning, preFilterExtHoursWarning.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_ext_hours_alarm, preFilterExtHoursAlarm.value, device)
        addParameterIfChanged(updated_parameters, preFilterExtMaxLifetimeWarningParamName, preFilterExtMaxLifetimeWarningPeriod.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_ext_max_lifetime_alarm, preFilterExtMaxLifetimeAlarm.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_ext_rpm_warning_deviation_pct, preFilterExtRpmWarningDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.pre_filter_ext_rpm_alarm_deviation_pct, preFilterExtRpmAlarmDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_sup_hours_warning, mainFilterSupHoursWarning.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_sup_hours_alarm, mainFilterSupHoursAlarm.value, device)
        addParameterIfChanged(updated_parameters, mainFilterSupMaxLifetimeWarningParamName, mainFilterSupMaxLifetimeWarningPeriod.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_sup_max_lifetime_alarm, mainFilterSupMaxLifetimeAlarm.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_sup_rpm_warning_deviation_pct, mainFilterSupRpmWarningDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_sup_rpm_alarm_deviation_pct, mainFilterSupRpmAlarmDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_ext_hours_warning, mainFilterExtHoursWarning.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_ext_hours_alarm, mainFilterExtHoursAlarm.value, device)
        addParameterIfChanged(updated_parameters, mainFilterExtMaxLifetimeWarningParamName, mainFilterExtMaxLifetimeWarningPeriod.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_ext_max_lifetime_alarm, mainFilterExtMaxLifetimeAlarm.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_ext_rpm_warning_deviation_pct, mainFilterExtRpmWarningDeviationPct.value, device)
        addParameterIfChanged(updated_parameters, awareParamNames.main_filter_ext_rpm_alarm_deviation_pct, mainFilterExtRpmAlarmDeviationPct.value, device)
        console.log(updated_parameters)
        return updated_parameters
    }

    function saveIfValidated(saveForGroup) {
        if (!validateParameters()) return

        let valuesToSave = getValuesToSave();
        if (Object.keys(valuesToSave).length === 0) {
            dispatch(addToast({
                id: "FilterSettingsInfoToast",
                type: toastTypes.info,
                title: "No changes",
                body: "No changes to parameters",
                timeSeconds: 3
            }))
            return
        }
        save(valuesToSave, saveForGroup);
    }

    const prepareResetData = (pre_filter_supply, pre_filter_extraction, main_filter_supply, main_filter_extraction) => {
        return {
            ["reset_filter"]: {
                "pre_filter_supply" : pre_filter_supply ? true : false,
                "pre_filter_extraction": pre_filter_extraction ? true: false,
                "main_filter_supply": main_filter_supply ? true : false,
                "main_filter_extraction": main_filter_extraction ? true : false
            }
        }
    }

    return (
        <div className={className}>
            <DeviceConfigTopInformation title={t('locales:filter_settings_page_header')}
                                        subTitle={t('locales:filter_settings_page_header_text')}/>

            {/* === Alarm or warning Log List*/}
            <SettingsSubHeader>{t('locales:filter_settings_header_filter_state')}</SettingsSubHeader>
            <div className='m-2'>
                <span className='m-sm-2 ms-2'>{t('locales:filter_settings_header_filter_state_text')}</span>
                <div className='flex-grow-1 border border-dim rounded-3 m-2'>
                    <div style={{fontSize: '12px', maxHeight: "450px"}} className='overflow-auto'>
                        {filterLatestStatus === "ALARM" &&
                            <div className='bg-danger-dim d-flex p-3 align-items-center ps-md-5'>
                                <ExclamationTriangle className="color-danger-bright" size={'3rem'}/>
                                <div className='d-block ms-3'>
                                    <p className='mb-1'><span
                                        className='fw-bold'>{t("settingspage:filter_log.activated_by")}:</span> {t("telemetry:SysAlarm_FilterAlarm")}
                                    </p>
                                </div>
                            </div>
                        }
                        {filterLatestStatus === "WARNING" &&
                            <div className='bg-warning-dim d-flex p-3 align-items-center ps-md-5'>
                                <ExclamationTriangle className="color-warning" size={'3rem'}/>
                                <div className='d-block ms-3'>
                                    <p className='mb-1'><span
                                        className='fw-bold'>{t("settingspage:filter_log.activated_by")}:</span> {t("telemetry:SysCon_FilterChangeNeeded")}
                                    </p>
                                </div>
                            </div>
                        }
                        {filterLatestStatus === "GOOD" &&
                            <div className='bg-success-dim d-flex p-3 align-items-center ps-md-5'>
                                <CheckCircle className="color-success" size={'3rem'}/>
                                <div className='d-block ms-3'>
                                    <p className='mb-1'><span
                                        className='fw-bold'>{t("settingspage:filter_log.latest_status")}:</span> {t("settingspage:filter_log.no_problems")}
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* === reset options table with radio buttons, as per screenshot (no arrows) */}
            <div className="p-3 my-3 w-100">
                <div className="table-responsive">
                    <Table size="sm" className="aware_filter_table table table-sm">
                    <thead>
                        <tr>
                            <th></th>
                            <th></th>
                            <th className="text-center hide-on-mobile">{t('locales:filter_settings_pre_filter_intake_time')}</th>
                            <th className="text-center hide-on-mobile">{t('locales:filter_settings_pre_filter_intake_operating_hours')}</th>
                            <th className="text-center hide-on-mobile">{t('locales:filter_settings_pre_filter_intake_flow_pressure')}</th>
                            <th className="text-center">{t('locales:filter_settings_pre_filter_intake')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{minHeight: '44px'}}>
                            <td rowSpan={2} className="align-middle fw-bold" style={{whiteSpace: 'nowrap', padding: 0, minHeight: '44px', width: '100px'}}>
                                {t('settingspage:filter_settings.pre_filter')}
                            </td>
                            <td className="align-middle" style={{padding: 0, minHeight: '44px'}}>{t('settingspage:filter_settings.intake')}</td>
                            <td className={preFilterSupplyLifetimeClass} style={{padding: 0, minHeight: '44px'}}>{pre_filter_supply_time_in_service} {t('common:units.months')}</td>
                            <td className={preFilterSupplyOperatingTimeClass} style={{padding: 0, minHeight: '44px'}}>{pre_filter_supply_operating_time_hours} {t('common:units.hours')}</td>
                            <td className={preFilterSupplyRpmDeviationClass} style={{padding: 0, minHeight: '44px'}}>{formatRoundedPercentage(pre_filter_supply_current_rpm_deviation)} %</td>
                            <td className="align-middle text-center" style={{padding: 0, minHeight: '44px'}}>
                                 <SwitchGroup
                                    className='w-200px m-auto'
                                    checked={preFilterSupplySelected}
                                    onChange={e => setPreFilterSupplySelected(e.target.checked)}
                                />
                            </td>  
                        </tr> 
                        <tr style={{minHeight: '44px'}}>
                            <td className="align-middle" style={{padding: 0, minHeight: '44px'}}>{t('settingspage:filter_settings.extract')}</td>
                            <td className={preFilterExtractLifetimeClass} style={{padding: 0, minHeight: '44px'}}>{pre_filter_extraction_time_in_service} {t('common:units.months')}</td>
                            <td className={preFilterExtractOperatingTimeClass} style={{padding: 0, minHeight: '44px'}}>{pre_filter_extraction_operating_time_hours} {t('common:units.hours')}</td>
                            <td className={preFilterExtractRpmDeviationClass} style={{padding: 0, minHeight: '44px'}}>{formatRoundedPercentage(pre_filter_extraction_current_rpm_deviation)} %</td>
                            <td className="align-middle text-center" style={{padding: 0, minHeight: '44px'}}>
                                 <SwitchGroup
                                    className='w-200px m-auto'
                                    checked={preFilterExtractionSelected}
                                    onChange={e => setPreFilterExtractionSelected(e.target.checked)}
                                />
                            </td>   
                        </tr>
                        <tr style={{minHeight: '44px'}}>
                            <td rowSpan={2} className="align-middle fw-bold" style={{whiteSpace: 'nowrap', padding: 0, minHeight: '44px', width: '100px'}}>
                                {t('settingspage:filter_settings.main_filter')}
                            </td>
                            <td className="align-middle" style={{padding: 0, minHeight: '44px'}}>{t('settingspage:filter_settings.intake')}</td>
                            <td className={mainFilterSupplyLifetimeClass} style={{padding: 0, minHeight: '44px'}}>{main_filter_supply_time_in_service} {t('common:units.months')}</td>
                            <td className={mainFilterSupplyOperatingTimeClass} style={{padding: 0, minHeight: '44px'}}>{main_filter_supply_operating_time_hours} {t('common:units.hours')}</td>
                            <td className={mainFilterSupplyRpmDeviationClass} style={{padding: 0, minHeight: '44px'}}>{formatRoundedPercentage(main_filter_supply_current_rpm_deviation)} %</td>
                            <td className="align-middle text-center" style={{padding: 0, minHeight: '44px'}}>
                                 <SwitchGroup
                                    className='w-200px m-auto'
                                    checked={mainFilterSupplySelected}
                                    onChange={e => setMainFilterSupplySelected(e.target.checked)}
                                />
                            </td>   
                        </tr>
                        <tr style={{minHeight: '44px'}}>
                            <td className="align-middle" style={{padding: 0, minHeight: '44px'}}>{t('settingspage:filter_settings.extract')}</td>
                            <td className={mainFilterExtractLifetimeClass} style={{padding: 0, minHeight: '44px'}}>{main_filter_extraction_time_in_service} {t('common:units.months')}</td>
                            <td className={mainFilterExtractOperatingTimeClass} style={{padding: 0, minHeight: '44px'}}>{main_filter_extraction_operating_time_hours} {t('common:units.hours')}</td>
                            <td className={mainFilterExtractRpmDeviationClass} style={{padding: 0, minHeight: '44px'}}>{formatRoundedPercentage(main_filter_extraction_current_rpm_deviation)} %</td>   
                            <td className="align-middle text-center" style={{padding: 0, minHeight: '44px'}}>
                                 <SwitchGroup
                                    className='w-200px m-auto'
                                    checked={mainFilterExtractionSelected}
                                    onChange={e => setMainFilterExtractionSelected(e.target.checked)}
                                />
                            </td>   
                        </tr>
                    </tbody>
                    </Table>
                </div>
                
                <div className="mt-3 d-flex justify-content-end">
                    <Button
                        variant='outline-secondary'
                        className='me-2'
                        onClick={() => {
                            save(prepareResetData(preFilterSupplySelected, preFilterExtractionSelected, mainFilterSupplySelected, mainFilterExtractionSelected), false);
                            setPreFilterSupplySelected(false);
                            setPreFilterExtractionSelected(false);
                            setMainFilterSupplySelected(false);
                            setMainFilterExtractionSelected(false);
                        }}
                    >
                        {t('settingspage:filter_settings.reset_selected_filters')}
                    </Button>
                    <Button
                        variant='outline-secondary'
                        onClick={() => {
                            save(prepareResetData(preFilterSupplySelected, preFilterExtractionSelected, mainFilterSupplySelected, mainFilterExtractionSelected), true);
                            setPreFilterSupplySelected(false);
                            setPreFilterExtractionSelected(false);
                            setMainFilterSupplySelected(false);
                            setMainFilterExtractionSelected(false);
                        }}
                    >
                        {t('settingspage:filter_settings.reset_selected_filters_group')}
                    </Button>
                </div>
            </div>

            {!isBasic(userRole) &&
                <Accordion defaultActiveKey={['0']} alwaysOpen>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t('locales:filter_settings_header_filter_monitoring')}</Accordion.Header>
                        <Accordion.Body>
                             <div className={'p-2 my-2 w-100 me-16px me-xs-0'}>
                                <div className="mt-sm-3 d-flex flex-wrap justify-content-xs-center"></div>
                                {/*Only visible below 1518px, that is where the grouped fields below jump line, so we group
                                filter test mode and filter flow alarm fields as well, so it doesn't look silly.
                                Might have been better with a css grid...*/}
                                <div className='d-1518px-none'>
                                    <div className='w-300px'>
                                        {renderFilterTestModeComboBox()}
                                    </div>
                                </div>

                                <div className='w-400px d-none d-1518px-block'>
                                    {renderFilterTestModeComboBox()}
                                </div>              
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    {!sameIntakeAsExtract && (
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>{t('locales:filter_settings_intake_filter_settings_header_header1')}</Accordion.Header>
                        <Accordion.Body>
                            <SettingsSubHeader>{t('settingspage:filter_settings.pre_filter_settings')}</SettingsSubHeader>
                            <div className="mt-sm-3 d-flex flex-wrap justify-content-xs-center">
                                    <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_pre_filter_header1')}
                                                className='p-4 m-auto'
                                                value={preFilterSupMaxLifetimeWarningPeriod.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setPreFilterSupMaxLifetimeWarningPeriod({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={preFilterSupMaxLifetimeWarningPeriod.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header1')}
                                                    popOverContent={intake_filter_pre_filter_max_life_time_warning_mark}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_pre_filter_header2')}
                                                className='p-4 m-auto'
                                                value={preFilterSupMaxLifetimeAlarm.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setPreFilterSupMaxLifetimeAlarm({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={preFilterSupMaxLifetimeAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header2')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_time_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_pre_filter_header3')}
                                                className='p-4 m-auto'
                                                value={preFilterSupHoursWarning.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setPreFilterSupHoursWarning({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={preFilterSupHoursWarning.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header3')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_operating_hours_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_pre_filter_header4')}
                                                className='p-4 m-auto'
                                                value={preFilterSupHoursAlarm.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setPreFilterSupHoursAlarm({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={preFilterSupHoursAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header4')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_operating_hours_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_pre_filter_header5')}
                                                className='p-4 m-auto'
                                                value={preFilterSupRpmWarningDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setPreFilterSupRpmWarningDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={preFilterSupRpmWarningDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header5')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_flow_pressure_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={"Flow/Pressure Alarm"}
                                                className='p-4 m-auto'
                                                value={preFilterSupRpmAlarmDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setPreFilterSupRpmAlarmDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={preFilterSupRpmAlarmDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_pre_filter_header6')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_flow_pressure_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>
                            </div>

                            <hr style={{width: '100%', margin: '32px 0'}} />
                            <SettingsSubHeader>{t('settingspage:filter_settings.main_filter_settings')}</SettingsSubHeader>
                            <div className="mt-sm-3 d-flex flex-wrap justify-content-xs-center">
                                                            <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header1')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupMaxLifetimeWarningPeriod.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setMainFilterSupMaxLifetimeWarningPeriod({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={mainFilterSupMaxLifetimeWarningPeriod.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header1')}
                                                    popOverContent={intake_filter_main_filter_max_life_time_warning_mark}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header2')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupMaxLifetimeAlarm.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setMainFilterSupMaxLifetimeAlarm({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={mainFilterSupMaxLifetimeAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header2')}
                                                    popOverContent={t('locales:intake_filter_main_filter_time_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alarm should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header3')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupHoursWarning.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setMainFilterSupHoursWarning({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={mainFilterSupHoursWarning.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header3')}
                                                    popOverContent={t('locales:intake_filter_main_filter_operating_hours_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header4')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupHoursAlarm.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setMainFilterSupHoursAlarm({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={mainFilterSupHoursAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header4')}
                                                    popOverContent={t('locales:intake_filter_main_filter_operating_hours_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alarm should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header5')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupRpmWarningDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setMainFilterSupRpmWarningDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={mainFilterSupRpmWarningDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header5')}
                                                    popOverContent={t('locales:intake_filter_main_filter_flow_pressure_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_intake_filter_settings_main_filter_header6')}
                                                className='p-4 m-auto'
                                                value={mainFilterSupRpmAlarmDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setMainFilterSupRpmAlarmDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={mainFilterSupRpmAlarmDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_intake_filter_settings_main_filter_header6')}
                                                    popOverContent={t('locales:intake_filter_main_filter_flow_pressure_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>
                            </div>
                            <div className='d-flex justify-content-end'>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <span style={{fontWeight: '500', marginRight: '12px'}}>{t('locales:filter_settings_intake_filter_settings_header_header2')}<PopOverDialog
                                                popOverTitel={t('locales:filter_settings_intake_filter_settings_header_header2')}
                                                popOverContent={t('locales:filter_settings_intake_filter_settings_header_question_mark')}
                                                trigger={['hover', 'touch']}
                                                contentAsHtml={true}/></span>
                                    <SwitchGroup
                                        checked={sameExtractAsIntake}
                                        onChange={e => setSameExtractAsIntake(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    )}
                    {!sameExtractAsIntake && (
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>{t('locales:filter_settings_extract_filter_settings_header_header1')}</Accordion.Header>
                        <Accordion.Body>
                            <SettingsSubHeader>{t('settingspage:filter_settings.pre_filter_settings')}</SettingsSubHeader>
                            <div className="mt-sm-3 d-flex flex-wrap justify-content-xs-center">
                                    <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header1')}
                                                className='p-4 m-auto'
                                                value={preFilterExtMaxLifetimeWarningPeriod.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setPreFilterExtMaxLifetimeWarningPeriod({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={preFilterExtMaxLifetimeWarningPeriod.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header1')}
                                                    popOverContent={extract_filter_pre_filter_max_life_time_warning_mark}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header2')}
                                                className='p-4 m-auto'
                                                value={preFilterExtMaxLifetimeAlarm.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setPreFilterExtMaxLifetimeAlarm({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={preFilterExtMaxLifetimeAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header2')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_time_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alarm should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header3')}
                                                className='p-4 m-auto'
                                                value={preFilterExtHoursWarning.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setPreFilterExtHoursWarning({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={preFilterExtHoursWarning.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header3')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_operating_hours_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header4')}
                                                className='p-4 m-auto'
                                                value={preFilterExtHoursAlarm.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setPreFilterExtHoursAlarm({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={preFilterExtHoursAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header4')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_operating_hours_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header5')}
                                                className='p-4 m-auto'
                                                value={preFilterExtRpmWarningDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setPreFilterExtRpmWarningDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={preFilterExtRpmWarningDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header5')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_flow_pressure_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_pre_filter_header6')}
                                                className='p-4 m-auto'
                                                value={preFilterExtRpmAlarmDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setPreFilterExtRpmAlarmDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={preFilterExtRpmAlarmDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_pre_filter_header6')}
                                                    popOverContent={t('locales:intake_filter_pre_filter_flow_pressure_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>
                            </div>

                            <hr style={{width: '100%', margin: '32px 0'}} />
                            <SettingsSubHeader>{t('settingspage:filter_settings.main_filter_settings')}</SettingsSubHeader>
                            <div className="mt-sm-3 d-flex flex-wrap justify-content-xs-center">
                                                            <div> {/* warning and alart should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header1')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtMaxLifetimeWarningPeriod.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setMainFilterExtMaxLifetimeWarningPeriod({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={mainFilterExtMaxLifetimeWarningPeriod.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header1')}
                                                    popOverContent={extract_filter_main_filter_max_life_time_warning_mark}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header2')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtMaxLifetimeAlarm.value}
                                                unit={t('common:units.months')}
                                                inputValue={(e) => setMainFilterExtMaxLifetimeAlarm({value: e.target.value})}
                                                placeholder={t('common:units.months')}
                                                messages={mainFilterExtMaxLifetimeAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header2')}
                                                    popOverContent={t('locales:extract_filter_main_filter_time_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alarm should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header_3')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtHoursWarning.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setMainFilterExtHoursWarning({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={mainFilterExtHoursWarning.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header_3')}
                                                    popOverContent={t('locales:extract_filter_main_filter_operating_hours_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header4')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtHoursAlarm.value}
                                                unit={t('common:units.hours')}
                                                inputValue={(e) => setMainFilterExtHoursAlarm({value: e.target.value})}
                                                placeholder={t('common:units.hours')}
                                                messages={mainFilterExtHoursAlarm.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header4')}
                                                    popOverContent={t('locales:extract_filter_main_filter_operating_hours_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>

                                    <div> {/* warning and alarm should be grouped */}
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header5')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtRpmWarningDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setMainFilterExtRpmWarningDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={mainFilterExtRpmWarningDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header5')}
                                                    popOverContent={t('locales:extract_filter_main_filter_flow_pressure_warning_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                        <div className='w-300px'>
                                            <InputGroupWithMessages
                                                label={t('locales:filter_settings_extract_filter_settings_main_filter_header6')}
                                                className='p-4 m-auto'
                                                value={mainFilterExtRpmAlarmDeviationPct.value}
                                                unit={'%'}
                                                inputValue={(e) => setMainFilterExtRpmAlarmDeviationPct({value: e.target.value})}
                                                placeholder={t('common:units.percentage')}
                                                messages={mainFilterExtRpmAlarmDeviationPct.messages}
                                                type='number'
                                                infoelement={<PopOverDialog
                                                    popOverTitel={t('locales:filter_settings_extract_filter_settings_main_filter_header6')}
                                                    popOverContent={t('locales:extract_filter_main_filter_flow_pressure_alarm_question_mark')}
                                                    trigger={['hover', 'touch']}
                                                    contentAsHtml={true}/>}
                                            />
                                        </div>
                                    </div>
                            </div>
                            <div className='d-flex justify-content-end'>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <span style={{fontWeight: '500', marginRight: '12px'}}>{t('locales:filter_settings_extract_filter_settings_header_header2')}<PopOverDialog
                                                popOverTitel={t('locales:filter_settings_extract_filter_settings_header_header2')}
                                                popOverContent={t('locales:filter_settings_extract_filter_settings_header_question_mark')}
                                                trigger={['hover', 'touch']}
                                                contentAsHtml={true}/></span>
                                    <SwitchGroup
                                        checked={sameIntakeAsExtract}
                                        onChange={e => setSameIntakeAsExtract(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    )}
                </Accordion>
            }
       
            {/*=== Button container*/}
            {!isBasic(userRole) &&
                <div className='d-inline-block float-end p-2'>
                    <Button variant='outline-secondary' onClick={() => saveIfValidated(false)}
                            className='me-2'>
                        {t('settingspage:save_device')}</Button>
                    <Button variant='outline-secondary' onClick={() => saveIfValidated(true)}>
                        {t('settingspage:save_group')}</Button>
                </div>
            }
        </div>
    )
}